import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Building2, ArrowLeft } from 'lucide-react'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import BrandForm from '../components/BrandForm'

export default function AddBrand() {
  const [existingIndustries, setExistingIndustries] = useState<string[]>([])
  const [brandCount, setBrandCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const { switchBrand, refreshUser } = useAuth()
  const navigate = useNavigate()

  // Fetch existing industries for autocomplete and brand count
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [industriesRes, brandsRes] = await Promise.all([
          api.get('/brands/industries'),
          api.get('/brands')
        ])
        setExistingIndustries(industriesRes.data.industries || [])
        setBrandCount(brandsRes.data.brands?.length || 0)
      } catch (error) {
        console.error('Failed to fetch data:', error)
        toast.error('Failed to load brands. Please refresh the page.')
        // Redirect to brands list if we can't determine brand count
        navigate('/settings/brands')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [navigate])

  const handleBrandFormSuccess = async (brand: any) => {
    // Brand created successfully (with either TRIAL or paid subscription)
    try {
      await refreshUser()
      await switchBrand(brand.id)
      navigate('/onboarding')
    } catch (error) {
      console.error('Failed to activate brand:', error)
      toast.error('Brand created but failed to activate')
      navigate('/settings/brands')
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate('/settings/brands')}
        className="mb-6 flex items-center text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Brands
      </button>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-6">
          <Building2 className="h-8 w-8 text-indigo-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add New Brand</h1>
            {brandCount === 0 ? (
              <p className="text-sm text-gray-600 mt-1">
                Your first brand includes a 7-day trial with full access
              </p>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                Select a subscription plan to activate this brand
              </p>
            )}
          </div>
        </div>

        <BrandForm
          onSuccess={handleBrandFormSuccess}
          onCancel={() => navigate('/settings/brands')}
          existingIndustries={existingIndustries}
          submitButtonText="Create Brand"
          isFirstBrand={brandCount === 0}
        />
      </div>
    </div>
  )
}
